import useAccountStatus from 'components/account/useAccountStatus';
import { useGetCurrentPlan } from 'components/billing/context/useBilling';
import { useHasProductAddOn } from 'components/billing/modules/plans/hooks/useHasProductAddOn';
import { useProductTrialStatus } from 'components/billing/modules/plans/hooks/useProductTrialStatus';
import { useShopperConfig } from 'components/shopper/components/hooks/queries';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useUser } from 'controllers/contexts/user';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useEntitlements } from 'providers/entitlements/EntitlementsProvider';

import { getShopperTrialStartedKey } from 'components/shopper/utils/helpers';

const ELIGIBLE_PLANS = ['Enterprise', 'Professional'];

/**
 * Provide information on the billing status of Shopper.
 */
export const useShopperBillingStatus = () => {
  const { hasLabsFlag } = usePSLabs();
  const { isEntitledTo } = useEntitlements();
  const { user } = useUser();
  const { data: currentPlan } = useGetCurrentPlan();
  const { shopperVerificationStatus } = useAccountStatus();
  const hasShopper = useHasProductAddOn('SHOPPER', 'SHOPPER_PLATFORM');
  const { onProductFreeTrial, hasExpiredProductFreeTrial } =
    useProductTrialStatus('SHOPPER_PLATFORM');
  const hasShopperTrialStartedInLocalStorage = useLocalStorage<boolean>({
    key: getShopperTrialStartedKey(user.shop_id),
    returnNull: false,
  });
  const { data: configData } = useShopperConfig();
  const hasSmsSales = useHasProductAddOn('SMS_SALES', 'SMS_SALES_FIXED_FEE');

  // This is the long term goal for Shopper eligibility - managed by the Entitlements service.
  // Currently, it's just !isCustomCart
  const isEntitledToShopper = hasLabsFlag('feature_gate_with_entitlements')
    ? isEntitledTo('ai::shopper::eligible')
    : true;

  // This is the short term goal - Automatic eligibility based on plan, compliance, and CS-led status.
  const eligibility =
    // This is the actual self-serve flag!
    hasLabsFlag('eligible_for_shopper_self_serve_release') &&
    // Has not been manually denied compliance status after signing up
    shopperVerificationStatus === 'ACTIVE' &&
    // Marked "Is CS Led" in Salesforce
    Boolean(user.is_cs_led) &&
    // Is on an eligible plan
    ELIGIBLE_PLANS.includes(currentPlan?.package?.name || '') &&
    // Does not have the SMS Sales product
    !hasSmsSales;

  // This is the current implementation - manually set LabsFlags for a pre-determined list of Shops.
  const overrideEligibility =
    hasLabsFlag('eligible_for_shopper_self_serve') &&
    shopperVerificationStatus === 'ACTIVE';

  const isEligible =
    isEntitledToShopper && (eligibility || overrideEligibility);

  const trialAvailable =
    isEligible && !hasShopper && !hasExpiredProductFreeTrial;

  return {
    /**
     * Is eligible for a Shopper plan:
     *  - Has not been manually denied compliance status
     *  - CS-led account
     *  - Enterprise, Professional, or SMS Sales plan
     */
    isEligible,

    /** Has a Shopper plan */
    hasProduct: hasShopper,

    /** Is eligible for a free trial of Shopper. Mutually exclusive with hasProduct. */
    trialAvailable,

    /** Is currently on a trial of Shopper */
    trialOngoing: onProductFreeTrial,

    /** Has an expired trial of Shopper */
    trialExpired: hasExpiredProductFreeTrial,

    /** Has a Shopper trial that has started by setting a local storage value but not yet activated */
    shopperTrialStartedButNeverActivated:
      trialAvailable &&
      hasShopperTrialStartedInLocalStorage &&
      !configData?.metadata?.last_go_live_at,

    /** Shopper has been activated in the past but is currently deactivated */
    wasActivatedButIsInactive:
      !configData?.is_live_mode &&
      configData?.metadata?.last_go_live_at !== null,

    hasShopperTrialStartedInLocalStorage,
  };
};
